import React, { useCallback, useState, useEffect } from "react";
import { useDropzone } from "react-dropzone";
import { gql, useQuery, useMutation } from "urql";
import stableStringify from "json-stable-stringify";

import save from "save-file";
import { Alert, AlertVariant } from "../../../components/src/Alert";
import { Link } from "wouter";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { cloneDeep } from "lodash";
import { Buffer } from "buffer";

import { FileIcon } from "../../../components/src/icons/FileIcon";
import { Attachment } from "../types.generated";
import { usePrevious } from "../../../components/src/common/Utils";

const GET_ATTACHMENT = gql`
    query getAttachment($_id: GraphQLObjectId!) {
        attachment(_id: $_id) {
            fileId
            fileName
            base64
            mimeType
        }
    }
`;

const CREATE_ATTACHMENT = gql`
    mutation createAttachment($clientId: GraphQLObjectId!, $input: CreateAttachmentInput!) {
        createAttachment(clientId: $clientId, input: $input) {
            fileId
            fileName
            mimeType
            updateTimestamp
        }
    }
`;
interface AttachmentFormProps {
    clientId: string;
    attachments: Partial<Attachment>[];
    onChange: (arg: Partial<Attachment>[]) => void;
}

export const AttachmentForm = ({ clientId, attachments, onChange }: AttachmentFormProps): React.ReactElement => {
    const [_, createAttachment] = useMutation(CREATE_ATTACHMENT);
    const [variables, setVariables] = useState<{ _id: string }>(null);

    const [{ data }] = useQuery({ query: GET_ATTACHMENT, variables, requestPolicy: "network-only", pause: variables ? false : true });
    const previousData = usePrevious(data);

    const [alert, setAlert] = useState<{ color: AlertVariant; visible: boolean; message: string }>({
        color: "info",
        visible: false,
        message: ""
    });
    const onDismissAlert = () => setAlert({ color: "info", visible: false, message: "" });

    useEffect(() => {
        if (data && stableStringify(data) !== stableStringify(previousData)) {
            const dataURI = "data:" + data.attachment.mimeType + ";base64," + data.attachment.base64;
            save(dataURI, data.attachment.fileName).catch((error) => {
                setAlert({ color: "danger", visible: true, message: error.toString() });
            });
        }
    }, [data, previousData]);

    const readFile = (inputFile) => {
        const reader = new FileReader();

        return new Promise((resolve, reject) => {
            reader.onerror = () => {
                reader.abort();
                console.log("file reading was aborted");
                reject();
            };

            reader.onload = async (event) => {
                const data = event.target.result as string;
                const result = {
                    fileName: inputFile.name,
                    base64: Buffer.from(data, "binary").toString("base64")
                };
                resolve(result);
            };
            reader.readAsBinaryString(inputFile);
        });
    };

    const onDrop = useCallback(
        async (acceptedFiles) => {
            const readFilesPromises = acceptedFiles.map(async (f) => await readFile(f));

            const newAttachments = await Promise.all(readFilesPromises)
                .then(async (files) => {
                    const attachmentsResult = [];
                    for (let i = 0; i < files.length; i++) {
                        await createAttachment({ clientId: clientId, input: files[i] })
                            .then(({ data }) => {
                                attachmentsResult.push({
                                    fileId: data.createAttachment.fileId,
                                    fileName: data.createAttachment.fileName,
                                    mimeType: data.createAttachment.mimeType,
                                    updateTimestamp: data.createAttachment.updateTimestamp
                                });
                            })
                            .catch((error) => {
                                setAlert({ color: "danger", visible: true, message: error.toString() });
                            });
                    }
                    return attachmentsResult;
                })
                .catch((error) => {
                    setAlert({ color: "danger", visible: true, message: error.toString() });
                });

            if (onChange) {
                const attachmentsUpdated: Partial<Attachment>[] = [...cloneDeep(attachments), ...(newAttachments as any[])];
                onChange(attachmentsUpdated);
            }
        },
        [onChange, attachments, clientId, createAttachment]
    );
    const maxSize = 10 * 1048576; // 10 MB
    const { getRootProps, getInputProps, fileRejections } = useDropzone({ onDrop, maxSize });

    if (fileRejections && fileRejections.length > 0) return <p className="text-red-600">{fileRejections[0].errors[0].message}</p>;

    return (
        <div id="transaction-attachment-form">
            <div className="flex flex-wrap">
                {attachments.map((attachment, index) => (
                    <div key={attachment.fileName + index} className="m-2 max-w-xs bg-white border border-gray-200 rounded-lg shadow-sm">
                        <div className="p-3 border-b border-gray-200">
                            <DropdownMenu.Root>
                                <DropdownMenu.Trigger asChild>
                                    <button className="p-1 hover:bg-gray-100 rounded">
                                        <FileIcon mimeType={attachment.mimeType} />
                                    </button>
                                </DropdownMenu.Trigger>
                                <DropdownMenu.Portal>
                                    <DropdownMenu.Content
                                        side="bottom"
                                        align="start"
                                        alignOffset={-8}
                                        sideOffset={5}
                                        collisionPadding={8}
                                        className="min-w-[200px] bg-white rounded-md shadow-lg border border-gray-200 p-1 z-[10000]"
                                    >
                                        <DropdownMenu.Item
                                            className="flex items-center px-2 py-1 text-sm rounded hover:bg-gray-100 cursor-pointer"
                                            onSelect={() => {
                                                if (onChange) {
                                                    // returns all attachments except deleted
                                                    onChange(attachments.filter((a) => a.fileId !== attachment.fileId));
                                                }
                                            }}
                                        >
                                            Delete attachment
                                        </DropdownMenu.Item>
                                        <DropdownMenu.Item
                                            className="flex items-center px-2 py-1 text-sm rounded hover:bg-gray-100 cursor-pointer"
                                            onSelect={async () => {
                                                setVariables({ _id: attachment.fileId });
                                            }}
                                        >
                                            Download attachment
                                        </DropdownMenu.Item>
                                        {attachment.mimeType === "application/pdf" ? (
                                            <DropdownMenu.Item
                                                className="flex items-center px-2 py-1 text-sm rounded hover:bg-gray-100 cursor-pointer"
                                                onSelect={async () => {
                                                    window.open(`/attachment/pdf/${attachment.fileId}`, "_blank");
                                                }}
                                            >
                                                View attachment
                                            </DropdownMenu.Item>
                                        ) : null}
                                    </DropdownMenu.Content>
                                </DropdownMenu.Portal>
                            </DropdownMenu.Root>
                        </div>
                        <div className="p-3">
                            {attachment.mimeType !== "application/pdf" ? (
                                <p className="text-sm text-gray-700">{attachment.fileName}</p>
                            ) : (
                                <p className="text-sm text-gray-700">
                                    <Link
                                        to={`/attachment/pdf/${attachment.fileId}`}
                                        target="_blank"
                                        className="text-sky-600 hover:text-sky-800"
                                    >
                                        {attachment.fileName}
                                    </Link>
                                </p>
                            )}
                        </div>
                    </div>
                ))}
            </div>

            <div className="m-2" {...getRootProps()}>
                <input {...getInputProps()} />
                <p className="p-3 rounded-md bg-gray-200 border border-stone-300 cursor-pointer hover:bg-gray-300 transition-colors">
                    Drag 'n' drop file here to upload as attachment
                </p>
            </div>

            {alert.visible ? (
                <Alert variant={alert.color} onClose={onDismissAlert} dismissible>
                    {alert.message}
                </Alert>
            ) : null}
        </div>
    );
};
